import React, { ComponentType, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import urlcat from "urlcat";
import AuthContext from "../Contexts/authContext";
import { AUTH_STATUS, AuthorizeResponse } from "../Graphql/types/auth";
import { LOCAL_STORAGE_KEYS } from "../Enums/localStorage";
import { authorizeQuery } from "../Graphql/querries/authQuery";
import { fetchGraphQL } from "../Graphql/utils";
import { setLocalStorageElement } from "../Utils/localStorageUtils";
import AlertBannerContext from "../Contexts/alertBannerContext";
import { LOGIN } from "../Components/Routes";
import { getEmployeeByIdQuery } from "../Graphql/querries/employeeQuery";
import { GetEmployeeInfoResponse } from "../Graphql/types/employee";

// Higher-Order Component
const WithAuth = <P extends object>(
  WrappedComponent: ComponentType<P>
): React.FC<P> => {
  const AuthenticatedComponent: React.FC<P> = (props) => {
    const navigate = useNavigate();
    const loginUrl = urlcat(LOGIN, {
      redirectPage: window.location.pathname
    });

    const { authContextInfo, updateAuthContextInfo } = useContext(AuthContext);
    const { setAlert } = useContext(AlertBannerContext);

    const { mutate: getEmployeeIfo } = useMutation<
      GetEmployeeInfoResponse,
      Error,
      string
    >({
      mutationKey: ["getEmployeeById"],
      mutationFn: (id) => fetchGraphQL(getEmployeeByIdQuery, { id }, setAlert),
      onSuccess: (response) => {
        updateAuthContextInfo({
          ...authContextInfo,
          info: response.getEmployeeById
        });
      }
    });

    const { mutate } = useMutation<AuthorizeResponse, Error>({
      mutationKey: ["authorize"],
      mutationFn: () => fetchGraphQL(authorizeQuery, { input: null }, setAlert),
      onSuccess: (response) => {
        const { status, accessToken, userId } = response.authorize;
        if (status !== AUTH_STATUS.AUTHENTICATED) {
          localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
          localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);

          navigate(loginUrl);
        } else if (accessToken)
          // set accesToken when it's returned when access token is returned back
          setLocalStorageElement(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        // fetching employee info
        getEmployeeIfo(userId);
        updateAuthContextInfo({
          ...authContextInfo,
          auth: {
            status,
            employeeId: userId
          }
        });
      },
      onError: () => {
        navigate(loginUrl);
      }
    });

    // Always redirect to previous page if user already logged in
    useEffect(() => {
      mutate();
    }, []);

    // Render the wrapped component if authenticated
    return <WrappedComponent {...props} />;
  };

  return AuthenticatedComponent;
};

export default WithAuth;
